import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "~/lib/utils";
import { Picture } from "~/components/ui/picture";
import { AutoAdvanceCollapse, AutoAdvanceTimer, useAutoAdvance } from "~/components/ui/auto-advance-accordion";
import { SectionTitle } from "~/components/ui/section-title";
import type { ProductHighlightsContent, ProductHighlightsItem } from "~/data/products/types";

interface HighlightsAccordionProps {
  content: ProductHighlightsContent;
  /** Shown under the list below lg only (the fleet PDP's Contact Sales / Build Quote). */
  ctas?: React.ReactNode;
}

// The `fleet:` classes restyle this for the fleet PDP's dark .fleet scope (Figma
// 10480:407909); the consumer PDPs never render inside it, so they are unaffected.
export function HighlightsAccordion({ content, ctas }: HighlightsAccordionProps) {
  const { t } = useTranslation("products");
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const { displayIndex, userHasInteracted, select } = useAutoAdvance(content.items.length, sectionRef);

  return (
    <section ref={sectionRef} id="features" className="fleet:bg-background fleet:py-10 lg:fleet:py-15 scroll-mt-20 bg-white py-15">
      <div className="wrap fleet:gap-6 lg:fleet:gap-14 flex flex-col gap-14">
        <div className="lg:fleet:max-w-none flex w-full flex-col items-center gap-2 lg:max-w-[732px] lg:items-start lg:gap-4">
          <div className="flex flex-col items-center gap-2 lg:items-start lg:gap-3">
            {/* bouncie-300 (used for this eyebrow elsewhere, e.g. FeaturesSection) is
                only 2.63:1 against white — fails WCAG's 3:1 minimum even at this large
                bold size. bouncie-500 passes at 4.82:1. */}
            <p className="eyebrow fleet:text-text-heading-alt">
              {t(content.eyebrow)}
            </p>
            <SectionTitle className="text-text-heading fleet:max-w-none max-w-[255px] text-center lg:max-w-none lg:text-left lg:whitespace-nowrap">
              {t(content.heading)}
            </SectionTitle>
          </div>
          <p className="text-text-body-alt fleet:text-text-body-secondary fleet:max-w-[251px] lg:fleet:max-w-[711px] lg:fleet:leading-[1.1] lg:text-md max-w-[299px] text-center text-sm lg:max-w-none lg:text-left">
            {t(content.description)}
          </p>
        </div>

        {/* lg:items-start (not items-center) — the accordion's height changes as
            items expand/collapse, and centering the row would make the image
            column visibly jump up and down in response. */}
        <div className="fleet:gap-[10px] lg:fleet:items-center xl:fleet:min-h-[423px] flex flex-col items-start gap-14 lg:flex-row lg:items-start lg:justify-between">
          {/* lg:mt-8 pushes just the accordion column down from the header block —
              the row's own gap (lg:gap-6 above) stays small so the image column
              can sit closer to the header instead of matching the accordion's
              spacing. */}
          {/* No inset or width cap at lg — the column just takes whatever space is left
              beside the image column (shrink-0 below, so this is the one that flexes).
              xl:ml-[85px]/xl:max-w (margin, not padding, so it doesn't eat into the
              393px text column Figma specs) only kick in once there's room to spare. */}
          <div className="lg:fleet:mt-0 xl:fleet:ml-0 xl:fleet:max-w-[458px] flex w-full flex-col lg:mt-8 xl:ml-[85px] xl:max-w-[426px]">
            {/* overflow-anchor:none — ported from bouncieweb's ToolsAccordion
                (TextSection). Without it, the browser's scroll anchoring tries to
                compensate for this list's height changing as items expand/collapse,
                which yanks the rest of the page's scroll position around instead of
                leaving it alone. */}
            <div className="fleet:gap-4 flex w-full flex-col [overflow-anchor:none]">
              {content.items.map((item, index) => (
                <HighlightsAccordionItem
                  key={index}
                  index={index}
                  item={item}
                  isActive={index === displayIndex}
                  userHasInteracted={userHasInteracted}
                  onClick={() => select(index)}
                />
              ))}
            </div>
          </div>

          {/* lg:h-[380px] — measured against the accordion column's own worst case now that
              it's unconstrained at lg (no fixed max-width squeezing descriptions into extra
              lines). Figma's real image is similarly taller than the accordion text column
              (same technique bouncieweb's ToolsAccordion uses), which is what keeps the
              row's overall height stable regardless of accordion state. */}
          {/* md:fleet:max-w — Figma has no tablet frame; full-bleed at md made the image bigger
              than desktop's, so cap it at the lg width and center it until the row layout. */}
          <div className="md:fleet:max-w-[493px] md:fleet:self-center xl:fleet:h-[418px] xl:fleet:w-[630px] xl:fleet:max-w-none relative order-first w-full shrink-0 lg:order-none lg:h-[380px] lg:w-[493px]">
            {content.items.some((item) => item.image) ? (
              <div className="relative aspect-[343/228] w-full lg:aspect-auto lg:h-full">
                {/* Figma's blurred "highlight" ellipse, drawn once here rather than baked into each image. */}
                <div aria-hidden className="fleet:block absolute top-[9.8%] left-[2.4%] hidden h-[68.2%] w-[95.2%] rounded-full bg-[#45546d] blur-[42px]" />
                {content.items.map((item, index) =>
                  item.image ? (
                    // Every image stays mounted and crossfades, so switching items never waits on a fetch.
                    <Picture
                      key={index}
                      src={item.image.src}
                      alt={t(item.image.alt)}
                      // The image column's width: full-bleed below md, 493px from md, 630px at xl (fleet).
                      sizes="(min-width: 1280px) 630px, (min-width: 768px) 493px, 100vw"
                      aria-hidden={index !== displayIndex}
                      className={cn(
                        "absolute top-[3.8%] left-[4.8%] h-[92.4%] w-[95.2%] object-contain transition-opacity duration-500",
                        index === displayIndex ? "opacity-100" : "opacity-0",
                      )}
                    />
                  ) : null,
                )}
                {(displayIndex == null || !content.items[displayIndex]?.image) && <FpoPlaceholder className="absolute inset-0" />}
              </div>
            ) : (
              <FpoPlaceholder className="fleet:aspect-[343/228] fleet:border-white/20 fleet:bg-white/5 lg:fleet:aspect-auto aspect-[343/274] lg:aspect-auto lg:h-full" />
            )}
          </div>
        </div>

        {ctas && <div className="fleet:pt-6 lg:hidden">{ctas}</div>}
      </div>
    </section>
  );
}

function FpoPlaceholder({ className }: { className?: string }) {
  return (
    <div className={cn("flex w-full items-center justify-center rounded-2xl border border-dashed border-neutral-200 bg-white/60", className)}>
      <div className="flex aspect-[85/44] w-[85%] items-center justify-center rounded-full" style={{ backgroundColor: "var(--neutral-80)" }}>
        <span className="font-display text-2xl font-extrabold tracking-wide text-[#e0219b]">FPO</span>
      </div>
    </div>
  );
}

interface HighlightsAccordionItemProps {
  item: ProductHighlightsItem;
  index: number;
  isActive: boolean;
  userHasInteracted: boolean;
  onClick: () => void;
}

function HighlightsAccordionItem({ item, index, isActive, userHasInteracted, onClick }: HighlightsAccordionItemProps) {
  const { t } = useTranslation("products");

  return (
    <div className="fleet:pl-6 relative pl-[33px]">
      {/* Timer sits in the left gutter, outside the divider line and text. Figma's "Carousel
          Time Elapsed Indicator" is a fixed 83px track 7px below the title (consumer 7981:60942,
          fleet 10480:408880): 23px down here, past the item's 16px top padding; 7px on the
          fleet PDP, whose items have none. */}
      <AutoAdvanceTimer
        isActive={isActive}
        userHasInteracted={userHasInteracted}
        className="fleet:top-[7px] fleet:w-2 fleet:bg-[#ebeef3] absolute top-[23px] left-0 h-[83px] w-[9px] rounded-full bg-neutral-100 transition-opacity duration-300"
        fillClassName="bg-bouncie-500 absolute top-0 w-full rounded-full"
        restingFillHeight="9px"
      />
      <button data-sid={`highlights-accordion-item:${index}`}
        type="button"
        onClick={onClick}
        className={cn(
          "fleet:gap-0 fleet:border-border fleet:pt-0 fleet:pb-[15px] flex w-full flex-col gap-2 border-b border-neutral-100 py-4 text-left",
          isActive && userHasInteracted ? "cursor-default" : "cursor-pointer",
        )}
      >
        <span className="font-display text-text-heading text-md fleet:font-sans fleet:font-bold fleet:leading-[1.1] fleet:tracking-[0.18px] font-extrabold">{t(item.title)}</span>
        <AutoAdvanceCollapse isActive={isActive}>
          <span className="text-text-body-secondary fleet:pt-4 block text-base leading-[1.5]">{t(item.description)}</span>
        </AutoAdvanceCollapse>
      </button>
    </div>
  );
}
